import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import ReactSelect, { components } from 'react-select';

import useModal from '../../hooks/useModal';
import useAsync from '../../hooks/useAsync';
import { classNames, getStandardAssetName } from '../helpers/functions';

import { getAssets } from '../../api/assets';

export const SelectAssets = ({
	key,
	className,
	assets,
	value,
	onChange,
	onClear,
	placeholder: defaultPlaceholder,
	skip = [],
	disabled,
	multi,
}) => {
	const [placeholder, setPlaceholder] = useState(
		value?.length
			? `Selected (${value?.length || 0})`
			: defaultPlaceholder || 'Type to search...'
	);

	const selectRef = useRef(null);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [input, setInput] = useState('');

	const { value: localAssets, execute: refreshAssets } = useAsync(getAssets, {
		immediate: false,
	});

	useEffect(() => {
		if (typeof assets === 'undefined') {
			refreshAssets({ pageSize: null });
		}
	}, []);

	const finalAssets = assets || localAssets?.rows;
	const result = finalAssets?.reduce(function (r, a) {
		r[a.manufacturerName || a?.manufacturer?.name] =
			r[a.manufacturerName || a?.manufacturer?.name] || [];
		r[a.manufacturerName || a?.manufacturer?.name].push(a);
		return r;
	}, {});

	const label = (asset) => ({
		label: getStandardAssetName(asset),
		value: asset.id,
		disabled: skip?.includes(asset.id),
	});

	const Option = (props) => {
		return (
			<div>
				<components.Option {...props} className="flex item-center">
					<input
						type="checkbox"
						checked={props.isSelected}
						onChange={() => null}
						className="mr-2 mt-1"
					/>{' '}
					<label>{props.label}</label>
				</components.Option>
			</div>
		);
	};

	const MultiValue = (props) => {
		return '';
	};

	const ValueContainer = (props) => {
		return (
			<div
				className="mx-3 flex-1 py-4 cursor-pointer"
				onClick={() => {
					setIsMenuOpen(true);
				}}
			>{`Selected (${props?.selectProps?.value?.length || 0})`}</div>
		);
	};

	const options = Object.keys(result || {})
		?.sort((a, b) => a?.localeCompare(b))
		?.map((key) => ({
			label: key,
			options: result[key]
				?.map(label)
				?.sort((a, b) => a?.label?.localeCompare(b?.label)),
		}));

	const selectedAsset = value ? finalAssets?.find((o) => o.id === value) : null;
	const selectedOption = selectedAsset ? label(selectedAsset) : null;

	return (
		<ReactSelect
			ref={selectRef}
			className={classNames('text-xs', className)}
			classNames={{
				control: (state) => 'rounded-md cursor-pointer',
			}}
			options={options}
			onChange={(value) => {
				setPlaceholder(`Selected (${value?.length || 0})`);
				onChange(value?.map((v) => v?.value));
			}}
			components={{ Option, MultiValue }}
			value={value?.map((id) => {
				return options?.flatMap((o) => o.options)?.find((o) => o.value === id);
			})}
			placeholder={placeholder}
			isOptionDisabled={(option) => option.disabled}
			isDisabled={disabled}
			disabled={disabled}
			controlShouldRenderValue={false}
			isMulti
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			allowSelectAll={true}
			onMenuOpen={() => setIsMenuOpen(true)}
			onMenuClose={() => setIsMenuOpen(false)}
			menuIsOpen={isMenuOpen}
			inputValue={input}
			onInputChange={(v, a) => {
				if (a.action === 'input-change') setInput(v);
			}}
		/>
	);
};

const SelectAsset = ({
	key,
	className,
	assets,
	value,
	onChange,
	onClear,
	placeholder,
	skip = [],
	disabled,
	locationId,
	isSearchable,
}) => {
	const { open, setOpen } = useModal('selectAsset');

	const { value: localAssets, execute: refreshAssets } = useAsync(getAssets, {
		immediate: false,
	});

	useEffect(() => {
		if (typeof assets === 'undefined') {
			refreshAssets({ pageSize: null });
		}
	}, []);

	const finalAssets = assets || localAssets?.rows;
	const result = finalAssets?.reduce(function (r, a) {
		r[a.manufacturerName || a?.manufacturer?.name] =
			r[a.manufacturerName || a?.manufacturer?.name] || [];
		r[a.manufacturerName || a?.manufacturer?.name].push(a);
		return r;
	}, {});

	const label = (asset) => ({
		label: getStandardAssetName(asset),
		value: asset.id,
		disabled: skip?.includes(asset.id),
		locationId: asset?.location?.id,
	});

	const options = Object.keys(result || {})
		?.map((key) => ({
			label: key,
			options: result[key]?.map(label),
		}))
		?.map((o) => {
			if (locationId) {
				const ops = o?.options?.filter(
					(asset) => asset?.locationId === locationId
				);
				o.options = ops;
			}
			return o;
		});

	const selectedAsset = value ? finalAssets?.find((o) => o.id === value) : null;
	const selectedOption = selectedAsset ? label(selectedAsset) : null;

	return (
		<ReactSelect
			className={classNames('text-xs', className)}
			classNames={{
				control: (state) => 'rounded-md cursor-pointer',
			}}
			options={options}
			onChange={({ value }) =>
				onChange(finalAssets?.find((o) => o.id === value))
			}
			value={selectedOption}
			placeholder={placeholder}
			isOptionDisabled={(option) => option.disabled}
			isDisabled={disabled}
			disabled={disabled}
			isSearchable={isSearchable}
		/>
	);

	return (
		<Listbox value={selectedAsset} onChange={onChange}>
			{({ open }) => (
				<>
					<Listbox.Label className="block text-sm font-medium text-gray-700">
						Select an asset
					</Listbox.Label>
					<div className="relative mt-1">
						<Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
							<span className="block truncate">
								{selectedAsset
									? getStandardAssetName(selectedAsset)
									: 'Select...'}
							</span>
							<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
								<ChevronUpDownIcon
									className="h-5 w-5 text-gray-400"
									aria-hidden="true"
								/>
							</span>
						</Listbox.Button>

						<Transition
							show={open}
							as={Fragment}
							leave="transition ease-in duration-100"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
								{finalAssets?.map((asset) => (
									<Listbox.Option
										key={asset.id}
										className={({ active }) =>
											classNames(
												active ? 'text-white bg-indigo-600' : 'text-gray-900',
												'relative cursor-default select-none py-2 pl-3 pr-9'
											)
										}
										value={asset}
									>
										{({ selected, active }) => (
											<>
												<span
													className={classNames(
														selected ? 'font-semibold' : 'font-normal',
														'block truncate'
													)}
												>
													{getStandardAssetName(asset)}
												</span>

												{selected ? (
													<span
														className={classNames(
															active ? 'text-white' : 'text-indigo-600',
															'absolute inset-y-0 right-0 flex items-center pr-4'
														)}
													>
														<CheckIcon className="h-5 w-5" aria-hidden="true" />
													</span>
												) : null}
											</>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</Transition>
					</div>
				</>
			)}
		</Listbox>
	);

	return (
		<>
			<div
				key={key}
				className="relative mt-1 cursor-pointer"
				onClick={() =>
					setOpen({
						assets: finalAssets,
						onSelect: (selected) => onChange(selected?.id),
						selected: value,
					})
				}
			>
				<div
					className={classNames(
						'pointer-events-none w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm',
						value ? ' text-gray-900' : ' text-gray-400'
					)}
				>
					{selectedAsset?.serialNumber || 'Select an asset'}
				</div>
				<div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
					{value ? (
						<XCircleIcon
							className="h-5 w-5 text-gray-900"
							aria-hidden="true"
							onClick={(e) => {
								e.stopPropagation();
								onChange(null);
							}}
						/>
					) : (
						<ChevronUpDownIcon
							className="h-5 w-5 text-gray-400"
							aria-hidden="true"
						/>
					)}
				</div>
			</div>
		</>
	);
};

export default SelectAsset;
